import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TYPES = ["Health", "Term Insurance", "Motor Insurance"];

export function InsuranceEnquiryForm() {
  const { user } = useAuth();
  const [type, setType] = useState("Health");
  const [name, setName] = useState(user?.name || "");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name."); return; }
    if (mobile.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setLoading(true);
    try {
      await api.post("/enquiries", {
        name: name.trim(), mobile: mobile.trim(), email: email.trim(),
        insurance_type: type, reason: message.trim(),
      });
      setDone(true);
      toast.success("Enquiry received — our team will reach out.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="enquiry-form-done" className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        🛡️ Thanks, {name}! We've noted your <b>{type}</b> enquiry and our team will call you on <b>{mobile}</b> soon.
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="insurance-enquiry-form"
      className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm space-y-3">
      <p className="font-heading font-semibold text-emerald-950">Insurance enquiry</p>
      <div className="space-y-1.5">
        <Label className="text-xs">I'm interested in</Label>
        <select data-testid="enquiry-type" value={type} onChange={(e) => setType(e.target.value)}
          className="w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm">
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Name</Label>
        <Input data-testid="enquiry-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Mobile</Label>
          <Input data-testid="enquiry-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9886260579" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Email <span className="text-zinc-400">(optional)</span></Label>
          <Input data-testid="enquiry-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Message <span className="text-zinc-400">(optional)</span></Label>
        <Textarea data-testid="enquiry-message" rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Looking for ₹1 Cr term cover" />
      </div>
      <Button type="submit" disabled={loading} data-testid="enquiry-submit"
        className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
        {loading ? "Sending…" : "Submit enquiry"}
      </Button>
    </form>
  );
}
